import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicLayout } from '../../components/PublicLayout';
import { useBooking } from '../../contexts/BookingContext';
import { publicAPI, paymentsAPI, imagesAPI } from '../../lib/api';
import { getCurrencySymbol } from '../../lib/currencies';
import { formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { PhoneInput } from '../../components/PhoneInput';
import countries from '../../lib/countries';
import {
  ArrowLeft,
  CreditCard,
  Banknote,
  Loader2,
  Calendar,
  Users,
  BedDouble,
  Lock,
  ShieldCheck,
  Globe,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

const MOLLIE_JS_URL = 'https://js.mollie.com/v1/mollie.js';

export default function Checkout() {
  const navigate = useNavigate();
  const { searchParams, selectedRoom, searchResults, setBooking } = useBooking();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [property, setProperty] = useState(null);
  const [mollieProfile, setMollieProfile] = useState(null);
  const [mollieReady, setMollieReady] = useState(false);
  const [cardError, setCardError] = useState('');
  const mollieRef = useRef(null);
  const cardFormRef = useRef(null);
  const mountedRef = useRef(false);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    notes: '',
  });
  const [phoneCountry, setPhoneCountry] = useState('US');

  // Build available currencies from room data
  const currencyOptions = useMemo(() => {
    if (!selectedRoom) return [];
    const defaultCurr = selectedRoom.currency || 'USD';
    const defaultRate = selectedRoom.price_per_night || 0;
    const options = [{ code: defaultCurr, rate: defaultRate }];
    const rates = selectedRoom.currency_rates || {};
    Object.entries(rates).forEach(([code, rate]) => {
      if (code !== defaultCurr) {
        options.push({ code, rate: Number(rate) });
      }
    });
    return options;
  }, [selectedRoom]);

  // Set initial currency when room loads
  useEffect(() => {
    if (selectedRoom && !selectedCurrency) {
      setSelectedCurrency(selectedRoom.currency || 'USD');
    }
  }, [selectedRoom, selectedCurrency]);

  // Calculate price based on selected currency
  const priceInfo = useMemo(() => {
    if (!selectedRoom || !selectedCurrency) return { perNight: 0, total: 0 };
    const nights = searchResults?.nights || 1;
    const defaultCurr = selectedRoom.currency || 'USD';
    if (selectedCurrency === defaultCurr) {
      return {
        perNight: selectedRoom.price_per_night || 0,
        total: selectedRoom.total_price || 0,
      };
    }
    const rates = selectedRoom.currency_rates || {};
    const rate = rates[selectedCurrency];
    if (rate) {
      return {
        perNight: Number(rate),
        total: Number(rate) * nights,
      };
    }
    return {
      perNight: selectedRoom.price_per_night || 0,
      total: selectedRoom.total_price || 0,
    };
  }, [selectedRoom, selectedCurrency, searchResults]);

  // Load Mollie.js script
  const loadMollieScript = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (window.Mollie) {
        resolve(window.Mollie);
        return;
      }
      const existing = document.querySelector(`script[src="${MOLLIE_JS_URL}"]`);
      if (existing) {
        existing.addEventListener('load', () => resolve(window.Mollie));
        return;
      }
      const script = document.createElement('script');
      script.src = MOLLIE_JS_URL;
      script.onload = () => resolve(window.Mollie);
      script.onerror = () => reject(new Error('Failed to load Mollie.js'));
      document.head.appendChild(script);
    });
  }, []);

  useEffect(() => {
    if (!selectedRoom || !searchResults) {
      navigate('/');
      return;
    }
    const fetchProperty = async () => {
      try {
        const res = await publicAPI.getProperty(selectedRoom.property_id || searchParams?.propertyId);
        setProperty(res.data);
        const propData = res.data?.property || res.data;
        const mode = propData?.payment_mode || 'pay_at_property';
        if (mode === 'online') setPaymentMethod('creditcard');

        // Fetch Mollie profile if online payments enabled
        if ((mode === 'online' || mode === 'both') && propData?.mollie_configured) {
          try {
            const profileRes = await paymentsAPI.getProfile(propData.id);
            setMollieProfile(profileRes.data);
          } catch (err) {
            console.error('Failed to load Mollie profile:', err);
          }
        }
      } catch {
        // Fallback
      }
    };
    fetchProperty();
  }, [selectedRoom, searchResults, navigate, searchParams]);

  // Derive payment mode from property
  const propData = property?.property || property;
  const paymentMode = propData?.payment_mode || 'pay_at_property';
  const showCash = paymentMode === 'pay_at_property' || paymentMode === 'both';
  const showOnline = (paymentMode === 'online' || paymentMode === 'both') && propData?.mollie_configured;

  // Initialize Mollie Components
  useEffect(() => {
    if (!mollieProfile?.profile_id || paymentMethod !== 'creditcard') return;
    if (mountedRef.current) return;

    let cancelled = false;

    const initMollie = async () => {
      try {
        const MollieFactory = await loadMollieScript();

        if (!mollieRef.current) {
          mollieRef.current = MollieFactory(mollieProfile.profile_id, {
            locale: 'en_US',
            testmode: mollieProfile.test_mode,
          });
        }

        if (cancelled) return;
        const mollie = mollieRef.current;

        await new Promise((r) => setTimeout(r, 600));
        if (cancelled || mountedRef.current) return;

        const container = document.getElementById('mollie-card');
        if (!container) return;

        const cardComponent = mollie.createComponent('card');
        cardComponent.mount('#mollie-card');
        cardFormRef.current = cardComponent;
        mountedRef.current = true;
        setMollieReady(true);
      } catch (err) {
        console.error('Failed to init Mollie Components:', err);
        if (!cancelled) toast.error('Failed to load payment form');
      }
    };
    initMollie();

    return () => {
      cancelled = true;
    };
  }, [mollieProfile, paymentMethod, loadMollieScript]);

  // Cleanup on payment method change away from creditcard
  useEffect(() => {
    if (paymentMethod !== 'creditcard' && mountedRef.current) {
      if (cardFormRef.current) {
        try { cardFormRef.current.unmount(); } catch {}
        cardFormRef.current = null;
      }
      mountedRef.current = false;
      setMollieReady(false);
    }
  }, [paymentMethod]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      let cardToken = null;

      // Get card token from Mollie Components for credit card payments
      if (paymentMethod === 'creditcard' && mollieRef.current) {
        const { token, error } = await mollieRef.current.createToken();
        if (error) {
          setCardError(error.message);
          toast.error(error.message || 'Invalid card details');
          setLoading(false);
          return;
        }
        cardToken = token;
      }

      const bookingData = {
        room_type_id: selectedRoom.id,
        check_in: searchResults.check_in,
        check_out: searchResults.check_out,
        guest: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          country: formData.country,
        },
        payment_method: paymentMethod,
        card_token: cardToken,
        total_amount: priceInfo.total,
        currency: selectedCurrency,
        redirect_url: `${window.location.origin}/booking/confirmation`,
        notes: formData.notes || null,
      };

      const response = await publicAPI.createBooking(bookingData);
      setBooking(response.data);

      // If Mollie returns a checkout URL (3D Secure or non-card methods), redirect
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }

      // Otherwise, go to confirmation
      toast.success('Booking confirmed!');
      navigate(`/booking/confirmation/${response.data.booking_reference}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create booking';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const resolveRoomImage = () => {
    if (selectedRoom?.gallery?.length > 0) return imagesAPI.getUrl(selectedRoom.gallery[0]);
    if (selectedRoom?.image_url) {
      if (selectedRoom.image_url.startsWith('http')) return selectedRoom.image_url;
      return imagesAPI.getUrl(selectedRoom.image_url);
    }
    return null;
  };

  if (!selectedRoom || !searchResults) return null;

  const roomImage = resolveRoomImage();
  const nights = searchResults.nights || Math.ceil(
    (new Date(searchResults.check_out) - new Date(searchResults.check_in)) / (1000 * 60 * 60 * 24)
  );

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="checkout-page">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Search Results
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Form */}
          <div className="lg:col-span-2 space-y-6">
            <form onSubmit={handleSubmit}>
              {/* Guest Details */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg">Guest Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Smith"
                        required
                        data-testid="guest-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        required
                        data-testid="guest-email-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <PhoneInput
                        value={formData.phone}
                        onChange={(val) => setFormData((prev) => ({ ...prev, phone: val }))}
                        selectedCountry={phoneCountry}
                        onCountryChange={setPhoneCountry}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country *</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <select
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          required
                          className="flex h-9 w-full rounded-md border border-input bg-transparent pl-9 pr-3 py-1 text-sm shadow-sm appearance-none cursor-pointer outline-none focus:ring-1 focus:ring-ring"
                          data-testid="guest-country-select"
                        >
                          <option value="" disabled>Select country</option>
                          {countries.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.flag}  {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Special Requests</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Any special requests..."
                      rows={1}
                      data-testid="guest-notes-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Payment Method */}
              <Card className="mb-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Payment Method</CardTitle>
                    <div className="relative inline-flex" data-testid="currency-select-wrapper">
                      <select
                        id="currency-select"
                        value={selectedCurrency}
                        onChange={(e) => setSelectedCurrency(e.target.value)}
                        className="h-9 min-w-[120px] rounded-md border border-input bg-white pl-3 pr-8 py-1 text-sm shadow-sm appearance-none cursor-pointer outline-none focus:ring-1 focus:ring-ring"
                        data-testid="currency-select"
                      >
                        {currencyOptions.map((opt) => (
                          <option key={opt.code} value={opt.code}>
                            {getCurrencySymbol(opt.code)} {opt.code}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={paymentMethod}
                    onValueChange={setPaymentMethod}
                    className="space-y-3"
                  >
                    {showCash && (
                      <div
                        className={`flex items-center space-x-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                          paymentMethod === 'cash'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/30'
                        }`}
                        onClick={() => setPaymentMethod('cash')}
                        data-testid="payment-cash"
                      >
                        <RadioGroupItem value="cash" id="cash" />
                        <Label htmlFor="cash" className="flex items-center gap-3 cursor-pointer flex-1">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Banknote className="h-5 w-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium">Pay at Property</p>
                            <p className="text-sm text-muted-foreground">Pay when you arrive</p>
                          </div>
                        </Label>
                      </div>
                    )}

                    {showOnline && (
                      <div
                        className={`flex items-center space-x-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                          paymentMethod === 'creditcard'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/30'
                        }`}
                        onClick={() => setPaymentMethod('creditcard')}
                        data-testid="payment-creditcard"
                      >
                        <RadioGroupItem value="creditcard" id="creditcard" />
                        <Label htmlFor="creditcard" className="flex items-center gap-3 cursor-pointer flex-1">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <CreditCard className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">Pay with Card</p>
                            <p className="text-sm text-muted-foreground">Credit or Debit Card - Secure payment</p>
                          </div>
                        </Label>
                      </div>
                    )}
                  </RadioGroup>

                  {/* Embedded Mollie Card Form */}
                  {paymentMethod === 'creditcard' && showOnline && (
                    <div className="mt-4">
                      {!mollieProfile ? (
                        <div className="flex items-center justify-center py-8 text-muted-foreground">
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Loading payment form...
                        </div>
                      ) : (
                        <>
                          <div
                            id="mollie-card"
                            className="mollie-card-wrapper"
                            data-testid="mollie-card-form"
                          />
                          {cardError && (
                            <p className="text-sm text-red-500 mt-2" data-testid="card-error">{cardError}</p>
                          )}
                          <div className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground">
                            <ShieldCheck className="h-3.5 w-3.5" />
                            <span>Payments secured by Mollie. Your card details are never stored on our servers.</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                className="w-full text-base py-6"
                disabled={loading || (paymentMethod === 'creditcard' && showOnline && !mollieReady)}
                data-testid="confirm-booking-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Processing...
                  </>
                ) : paymentMethod === 'cash' ? (
                  'Confirm Booking'
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Pay {getCurrencySymbol(selectedCurrency)} {priceInfo.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Right: Booking Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8">
              <CardHeader>
                <CardTitle className="text-lg">Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Room Image */}
                <div className="aspect-video rounded-lg overflow-hidden">
                  {roomImage ? (
                    <img src={roomImage} alt={selectedRoom.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <BedDouble className="h-10 w-10 text-muted-foreground" />
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="font-semibold">{selectedRoom.name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedRoom.property_name}</p>
                </div>

                <Separator />

                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {formatDate(searchResults.check_in)} - {formatDate(searchResults.check_out)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>{searchResults.guests || searchParams?.guests || 1} Guest(s)</span>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {getCurrencySymbol(selectedCurrency)} {priceInfo.perNight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} x {nights} night{nights > 1 ? 's' : ''}
                    </span>
                    <span>{getCurrencySymbol(selectedCurrency)} {priceInfo.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-semibold text-lg" data-testid="total-amount">
                    <span>Total</span>
                    <span>{getCurrencySymbol(selectedCurrency)} {priceInfo.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
